import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { ArrowBigUp, MapPin } from 'lucide-react'
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  onSnapshot,
} from 'firebase/firestore'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { db, auth } from '@/lib/firebase'
import { CountryFlag } from '@/lib/countryFlags'
import {
  CITY_UPVOTES_COLLECTION,
  cityUpvoteFirestoreDocId,
  normalizeFanUpvotes,
  toggleCityUpvoteFirestore,
} from '@/lib/cityUpvotes'
import { ArtistSearchBar } from '@/components/shared/ArtistSearchBar'
import { RequestCityDialog } from '@/components/city/RequestCityDialog'
import { AuthModal } from '@/components/auth/AuthModal'
import { useAuthState } from '@/hooks/useAuth'
import type { Artist } from '@/types/models'

interface CityData {
  city: string
  country: string
  artistCount: number
  progress: number
}

type PendingAuthAction =
  | { kind: 'request_city' }
  | { kind: 'upvote'; cityData: CityData }

export default function CityRequestsPage() {
  const { user, loading: authLoading } = useAuthState()
  const [cities, setCities] = useState<CityData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [viewerUpvoted, setViewerUpvoted] = useState<Record<string, boolean>>({})
  const [pendingKey, setPendingKey] = useState<string | null>(null)
  const [citySearchQuery, setCitySearchQuery] = useState('')
  const [requestCityOpen, setRequestCityOpen] = useState(false)
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const pendingAfterAuthRef = useRef<PendingAuthAction | null>(null)

  const submitFanUpvote = useCallback(async (cityData: CityData, uid: string) => {
    const cityKey = `${cityData.city}|${cityData.country}`
    setPendingKey(cityKey)
    try {
      await toggleCityUpvoteFirestore({
        cityKey,
        city: cityData.city,
        country: cityData.country,
        userId: uid,
      })
    } catch (err) {
      console.error('City upvote failed:', err)
      toast.error('Could not update upvote')
    } finally {
      setPendingKey(null)
    }
  }, [])

  const handleAuthModalOpenChange = useCallback((open: boolean) => {
    setAuthModalOpen(open)
  }, [])

  const handleAuthModalSuccess = useCallback(() => {
    const pending = pendingAfterAuthRef.current
    pendingAfterAuthRef.current = null
    setAuthModalOpen(false)
    if (!pending) return
    if (pending.kind === 'request_city') {
      setRequestCityOpen(true)
      return
    }
    const authedUid = auth.currentUser?.uid
    if (!authedUid) return
    void submitFanUpvote(pending.cityData, authedUid)
  }, [submitFanUpvote])

  const openRequestCityFlow = useCallback(() => {
    if (authLoading) return
    if (user) {
      setRequestCityOpen(true)
      return
    }
    pendingAfterAuthRef.current = { kind: 'request_city' }
    setAuthModalOpen(true)
  }, [authLoading, user])

  const openUpvoteFlow = useCallback(
    (cityData: CityData) => {
      if (authLoading) return
      if (user?.uid) {
        void submitFanUpvote(cityData, user.uid)
        return
      }
      pendingAfterAuthRef.current = { kind: 'upvote', cityData }
      setAuthModalOpen(true)
    },
    [authLoading, user?.uid, submitFanUpvote]
  )

  const filteredCities = useMemo(() => {
    const q = citySearchQuery.trim().toLowerCase()
    if (!q) return cities
    return cities.filter(
      (c) =>
        c.city.toLowerCase().includes(q) || c.country.toLowerCase().includes(q)
    )
  }, [cities, citySearchQuery])

  useEffect(() => {
    const fetchCityData = async () => {
      try {
        setIsLoading(true)

        const q = query(
          collection(db, 'artists'),
          where('onCityWaitlist', '==', true)
        )

        const snapshot = await getDocs(q)
        const artists = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        })) as Artist[]

        const cityMap = new Map<string, { city: string; country: string; count: number }>()

        artists.forEach((artist) => {
          const city = artist.location?.city || 'Unknown'
          const country = artist.location?.country || 'Unknown'
          const key = `${city}|${country}`

          if (!cityMap.has(key)) {
            cityMap.set(key, { city, country, count: 0 })
          }
          const cityEntry = cityMap.get(key)!
          cityEntry.count += 1
        })

        const cityDataArray: CityData[] = Array.from(cityMap.values())
          .map((cityData) => ({
            city: cityData.city,
            country: cityData.country,
            artistCount: cityData.count,
            progress: (cityData.count / 10) * 100,
          }))
          .sort((a, b) => b.artistCount - a.artistCount)

        setCities(cityDataArray)
      } catch (error) {
        console.error('Error fetching city data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchCityData()
  }, [])

  useEffect(() => {
    if (!user || !cities.length) {
      setViewerUpvoted({})
      return
    }

    const uid = user.uid
    const unsubs: (() => void)[] = []
    cities.forEach((c) => {
      const cityKey = `${c.city}|${c.country}`
      const id = cityUpvoteFirestoreDocId(cityKey)
      const rollupRef = doc(db, CITY_UPVOTES_COLLECTION, id)
      unsubs.push(
        onSnapshot(rollupRef, (snap) => {
          const ids = normalizeFanUpvotes(snap.data()?.upvotes)
          setViewerUpvoted((prev) => ({
            ...prev,
            [cityKey]: ids.includes(uid),
          }))
        })
      )
    })

    return () => unsubs.forEach((unsub) => unsub())
  }, [user, cities])

  return (
    <div className="min-h-screen bg-background transition-colors pb-bottom-nav">
      <div className="phone-container-padded flex flex-col gap-3 pt-safe-native-top-cushion md:pt-4 pb-20">
        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex min-w-0 flex-col gap-2">
            <h1 className="text-page-title text-foreground">Cities available next</h1>
            <p className="text-sm text-foreground leading-relaxed">
              We are releasing a city on the app once 10 artists from that city are on GetMoreTats.
            </p>
            <p className="text-sm text-foreground leading-relaxed">
              Until then you can upvote your city—or be the first to request it!
            </p>
          </div>
          <Button
            type="button"
            variant="default"
            className="h-12 w-full gap-2 rounded-2xl text-base font-medium shadow-none"
            onClick={() => openRequestCityFlow()}
            disabled={authLoading}
          >
            <MapPin className="h-4 w-4 shrink-0" aria-hidden />
            Request a city!
          </Button>
          <RequestCityDialog open={requestCityOpen} onOpenChange={setRequestCityOpen} />
          <AuthModal
            isOpen={authModalOpen}
            onOpenChange={handleAuthModalOpenChange}
            onSuccess={handleAuthModalSuccess}
            defaultMode="signup"
            source="city_requests"
          />
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-muted rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : cities.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No cities in progress yet</p>
          </div>
        ) : (
          <>
            <ArtistSearchBar
              filterAsYouType
              onQueryChange={setCitySearchQuery}
              placeholder="Search cities or countries…"
            />
            {filteredCities.length === 0 ? (
              <div className="rounded-xl border border-border/70 bg-card px-4 py-10 text-center text-sm text-muted-foreground">
                No cities match your search
              </div>
            ) : (
          <div className="space-y-2">
            {filteredCities.map((cityData) => {
              const cardKey = `${cityData.city}|${cityData.country}`
              const isUpvoted = viewerUpvoted[cardKey] === true
              const isPending = pendingKey === cardKey
              return (
                <div
                  key={cardKey}
                  className="relative bg-card rounded-2xl border border-border/70 p-4 hover:border-border/80 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <CountryFlag
                        country={cityData.country === 'Unknown' ? undefined : cityData.country}
                        size="lg"
                        className="shrink-0"
                      />
                      <h2 className="text-base font-semibold truncate">{cityData.city}</h2>
                    </div>
                    <Button
                      type="button"
                      variant="default"
                      onClick={() => openUpvoteFlow(cityData)}
                      disabled={authLoading || isPending}
                      aria-pressed={isUpvoted}
                      aria-label={isUpvoted ? 'Remove upvote' : 'Upvote city'}
                      className={cn(
                        'h-auto shrink-0 gap-1.5 rounded-2xl px-3 py-2 text-xs font-medium shadow-none',
                        'disabled:pointer-events-none disabled:opacity-50'
                      )}
                    >
                      <ArrowBigUp
                        className={cn(
                          'h-4 w-4 shrink-0 stroke-primary-foreground transition-all duration-300 ease-out',
                          isUpvoted
                            ? 'fill-primary-foreground stroke-primary-foreground'
                            : 'fill-transparent'
                        )}
                        strokeWidth={2}
                        aria-hidden
                      />
                      <span
                        key={isUpvoted ? 'upvoted-label' : 'upvote-label'}
                        className="animate-in fade-in zoom-in-95 duration-200"
                      >
                        {isUpvoted ? 'Upvoted' : 'Upvote'}
                      </span>
                    </Button>
                  </div>

                  <div className="mb-3">
                    <span className="text-sm font-medium text-foreground">
                      {cityData.artistCount} of 10 artists
                    </span>
                  </div>

                  <div className="flex gap-1.5">
                    {[...Array(10)].map((_, index) => (
                      <div
                        key={index}
                        className={`flex-1 h-3 rounded transition-colors duration-300 ${
                          index < cityData.artistCount ? 'bg-primary' : 'bg-muted'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
